import { Checkbox } from '#/modules/commonComponents/checkbox';
import MaterialNameStack from '#/modules/imsMgmt/receipts/shared/MaterialNameStack';
import {
  formatAdjustmentTypeLabel,
} from '#/modules/imsMgmt/receipts/stockAdjustment/helper/adjustmentStatus';

function formatWarehouseLocation(warehouse) {
  if (!warehouse) {
    return '-';
  }

  const location = warehouse.location;
  const warehouseLabel = warehouse.code
    ? `${warehouse.code} ${warehouse.name ?? ''}`.trim()
    : (warehouse.name ?? '-');

  if (!location) {
    return warehouseLabel;
  }

  const locationLabel = location.code
    ? `${location.code} ${location.name ?? ''}`.trim()
    : (location.name ?? '-');

  return `${warehouseLabel} / ${locationLabel}`;
}

export function buildStockAdjustmentLineColumns({
  showCompletedFields = false,
  canPrintLabel = false,
  selectedInventoryStockIds,
  selectAllChecked = false,
  selectAllDisabled = true,
  onToggleLine,
  onToggleAll,
} = {}) {
  const columns = [];

  if (canPrintLabel) {
    columns.push({
      id: 'lead',
      header: () => (
        <div className="flex items-center justify-center">
          <Checkbox
            checked={selectAllChecked}
            disabled={selectAllDisabled}
            onCheckedChange={onToggleAll}
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
            aria-label="全選"
          />
        </div>
      ),
      size: 48,
      enableSorting: false,
      meta: { noTruncate: true },
      cell: ({ row }) => {
        const stockId = row.original.invStock?.id;
        if (!stockId) {
          return null;
        }

        return (
          <div className="flex justify-center">
            <Checkbox
              checked={selectedInventoryStockIds.has(stockId)}
              onCheckedChange={(value) => {
                onToggleLine(stockId, value === true);
              }}
              onClick={(event) => event.stopPropagation()}
              onMouseDown={(event) => event.stopPropagation()}
              aria-label="選取庫存標籤"
            />
          </div>
        );
      },
    });
  }

  columns.push(
    {
      id: 'stockNo',
      header: '庫存編號',
      size: 140,
      enableSorting: false,
      cell: ({ row }) => row.original.invStock?.stockNo
        || row.original.invStock?.id
        || '-',
    },
    {
      id: 'mtr',
      header: '物料',
      size: 180,
      enableSorting: false,
      meta: { cellClassName: 'td_left' },
      cell: ({ row }) => {
        const mtr = row.original.mtr;
        if (!mtr) {
          return '-';
        }
        return <MaterialNameStack name={mtr.name} code={mtr.mtrNo} />;
      },
    },
    {
      id: 'warehouse',
      header: '倉／儲位',
      size: 180,
      enableSorting: false,
      meta: { cellClassName: 'td_left' },
      cell: ({ row }) => formatWarehouseLocation(row.original.warehouse),
    },
    {
      id: 'adjustmentType',
      header: '類型',
      size: 100,
      enableSorting: false,
      cell: () => formatAdjustmentTypeLabel('QTY_ADJUST'),
    },
    {
      id: 'baseQty',
      header: '調整量',
      size: 100,
      enableSorting: false,
      meta: { cellClassName: 'text-right' },
      cell: ({ row }) => {
        const qty = row.original.baseQty ?? row.original.adjustmentQty;
        const unit = row.original.unit?.code;
        if (qty == null) {
          return '-';
        }
        const n = Number(qty);
        const signed = Number.isFinite(n) && n > 0 ? `+${n}` : String(qty);
        return unit ? `${signed} ${unit}` : signed;
      },
    },
    {
      id: 'remark',
      header: '備註',
      size: 140,
      enableSorting: false,
      meta: { cellClassName: 'td_left' },
      cell: ({ row }) => row.original.remark || '-',
    },
  );

  if (showCompletedFields) {
    columns.push(
      {
        id: 'qtyBefore',
        header: '調整前',
        size: 112,
        enableSorting: false,
        meta: { cellClassName: 'text-right' },
        cell: ({ row }) => (
          row.original.invTransaction?.qtyBefore
          ?? row.original.qtyBefore
          ?? '-'
        ),
      },
      {
        id: 'qtyAfter',
        header: '調整後',
        size: 112,
        enableSorting: false,
        meta: { cellClassName: 'text-right' },
        cell: ({ row }) => (
          row.original.invTransaction?.qtyAfter
          ?? row.original.qtyAfter
          ?? '-'
        ),
      },
      {
        id: 'txCount',
        header: '異動筆數',
        size: 120,
        enableSorting: false,
        meta: { cellClassName: 'text-right' },
        cell: ({ row }) => {
          if (row.original.invTransaction) {
            return 1;
          }
          const legacy = row.original.invTransactions;
          return Array.isArray(legacy) ? legacy.length : 0;
        },
      },
    );
  }

  return columns;
}
